import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
'./ui/accordion';
import { useToast } from '../hooks/use-toast';
import {
  Package,
  Globe,
  Shield,
  TrendingUp,
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  Leaf,
  Award,
  Users,
  Building2,
  FileText,
  Target,
  Handshake,
  Menu,
  X } from
'lucide-react';
import { mockSubmitContactForm, mockSubmitSampleRequest, coffeeProducts, cupProfiles } from '../mock';

const Home = () => {
  const { toast } = useToast();
  const [contactForm, setContactForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    inquiryType: '', // New: commercial sheets, cacao, special request
    selectedCoffees: [], // New: for commercial sheets request
    message: ''
  });

  const [sampleForm, setSampleForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    selectedCoffees: [], // Updated: will include defect levels
    quantity: '',
    additionalNotes: ''
  });

  const [showSampleForm, setShowSampleForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await mockSubmitContactForm(contactForm);
      toast({
        title: "Message Sent",
        description: response.message
      });
      setContactForm({
        name: '',
        company: '',
        email: '',
        phone: '',
        country: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSampleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await mockSubmitSampleRequest(sampleForm);
      toast({
        title: "Sample Request Received",
        description: response.message
      });
      setSampleForm({
        name: '',
        company: '',
        email: '',
        phone: '',
        country: '',
        selectedCoffees: [],
        quantity: '',
        additionalNotes: ''
      });
      setShowSampleForm(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit sample request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCoffeeToggle = (coffeeName, defects, formType = 'sample') => {
    const coffeeId = `${coffeeName} - ${defects} defects`;
    
    if (formType === 'sample') {
      setSampleForm((prev) => ({
        ...prev,
        selectedCoffees: prev.selectedCoffees.includes(coffeeId)
          ? prev.selectedCoffees.filter((c) => c !== coffeeId)
          : [...prev.selectedCoffees, coffeeId]
      }));
    } else if (formType === 'contact') {
      setContactForm((prev) => ({
        ...prev,
        selectedCoffees: prev.selectedCoffees.includes(coffeeId)
          ? prev.selectedCoffees.filter((c) => c !== coffeeId)
          : [...prev.selectedCoffees, coffeeId]
      }));
    }
  };

  // Helper functions for message field labels and placeholders
  const getMessageLabel = (inquiryType) => {
    if (inquiryType === 'special-request') return 'Describe Your Special Request';
    if (inquiryType === 'commercial-sheets') return 'Additional Notes (Optional)';
    return 'Message';
  };

  const getMessagePlaceholder = (inquiryType) => {
    if (inquiryType === 'special-request') return 'Describe your specific requirements (origin, processing, profile, volume, etc.)...';
    if (inquiryType === 'commercial-sheets') return 'Additional notes (optional)...';
    return 'Tell us about your requirements...';
  };

  const isMessageRequired = (inquiryType) => {
    return inquiryType !== 'commercial-sheets' && !!inquiryType;
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSample = () => {
    setShowSampleForm(true);
    setTimeout(() => {
      document.getElementById('sample-request')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_avila-coffee-export/artifacts/dxpkxxys_Logo.png"
              alt="Avila Trade Group"
              className="h-16 bg-white rounded-sm px-2" />

          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">About</a>
            <a href="#products" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">Products</a>
            <a href="#contact" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">Contact</a>
            <Button onClick={scrollToSample} className="bg-amber-600 hover:bg-amber-700 text-white">
              Request Sample
            </Button>
          </nav>
          <button
            type="button"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen &&
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu-panel">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              <a
              href="#about"
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors font-medium py-3 px-2 rounded-md"
              data-testid="mobile-menu-about">
                About
              </a>
              <a
              href="#products"
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors font-medium py-3 px-2 rounded-md"
              data-testid="mobile-menu-products">
                Products
              </a>
              <a
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors font-medium py-3 px-2 rounded-md"
              data-testid="mobile-menu-contact">
                Contact
              </a>
              <Button
              onClick={() => {
                setMobileMenuOpen(false);
                scrollToSample();
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white mt-2 w-full"
              data-testid="mobile-menu-request-sample">
                Request Sample
              </Button>
            </nav>
          </div>
        }
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1633437805600-2c58bf56663c)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>

          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-800/70"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Venezuelan Green Coffee <br />
            <span className="text-amber-400">for Importers and Roasters</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl mb-8 md:mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed">Avila Trade Group sources and exports Venezuelan green coffee washed and natural arabica for importers, traders, and roasters seeking reliable supply, lot-specific specifications, and workable commercial terms.

          </p>
          <div className="flex gap-3 sm:gap-4 justify-center flex-wrap">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="bg-amber-600 hover:bg-amber-700 text-white text-sm sm:text-base md:text-lg px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">

              Request Lot Specifications
            </Button>
            <Button
              size="lg"
              onClick={scrollToSample}
              className="bg-amber-600 hover:bg-amber-700 text-white text-sm sm:text-base md:text-lg px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">

              Request Samples
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={scrollToContact}
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 text-sm sm:text-base md:text-lg px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">

              Discuss Sourcing Needs
            </Button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white" />
        </div>
      </section>

      {/* Trust / Value Strip */}
      <section className="py-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Reliable Export Execution</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Export-ready coordination from Venezuelan origin to destination, with transparent logistics, documentation, and commercial terms.
              </p>
            </div>
            
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Lot-Specific Quality Information</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">Every coffee offered with detailed specifications by lot, sensory summary, physical specs, availability, and process information.

              </p>
            </div>
            
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Flexible Sourcing Model</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">Beyond core offerings, we source to buyer requirements, process, preparation level, cup profile, volume, and application.

              </p>
            </div>
            
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Long-Term Supply Partnerships</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Built for buyers who need dependable Venezuelan coffee access over time, not one-off transactions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                About Avila Trade Group
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">Avila Trade Group was founded on a straightforward belief: Venezuelan coffee has earned its place on the global stage and it's been missing from it for too long.

              </p>
              <p className="">

              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">We work as sourcing partners for importers and roasters who need dependable access to Venezuelan coffee with quality, availability, and workable terms.</p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">Our focus is on steady supply, long-term relationships, and matching coffees to buyer requirements. Beyond our core portfolio, we can also source according to target cup profile, preparation level, volume, and application.</p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Mission</h4>
                    <p className="text-gray-600">To provide international importers and roasters with dependable access to Venezuelan green coffee through transparent grading, flexible sourcing, and export execution.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Vision</h4>
                    <p className="text-gray-600">To position Venezuelan coffee as a trusted and recognized origin in global markets, allowing buyers worldwide to access the unique character and commercial potential of Venezuelan coffee through a professional and reliable supply network.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1613690399151-65ea69478674"
                alt="International Trade"
                className="rounded-lg shadow-2xl" />

            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8">Our Coffee Range</h2>
          </div>
          
          {/* Intro */}
          <div className="max-w-4xl mx-auto mb-16">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              We supply Venezuelan green coffee across multiple preparation levels, processing methods, and commercial profiles, with lot-specific specifications available upon request.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our current portfolio includes washed arabica and natural arabica for importers, traders, and roasters seeking reliable commercial supply from Venezuelan origin. Final quality documentation is shared by lot and includes sensory summary, physical specs, process information, and availability.
            </p>
          </div>

          {/* Coffee Products - Direct Display */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {coffeeProducts.map((product) =>
            <Card key={product.id} className="border-slate-200 hover:shadow-xl transition-all duration-300">
                    <CardHeader>
                      <CardTitle className="text-2xl text-slate-900">{product.name}</CardTitle>
                      <CardDescription className="text-amber-600 font-semibold text-lg">{product.grade}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 mb-4 leading-relaxed">{product.description}</p>
                      
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-900 mb-2 text-sm">Typical Specs:</h4>
                        <ul className="space-y-2">
                          {product.characteristics.map((char) =>
                        <li key={char} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <span>{char}</span>
                            </li>
                        )}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2 text-sm">Best Suited For:</h4>
                        <ul className="space-y-1">
                          {product.bestFor.map((use) =>
                        <li key={use} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span>{use}</span>
                            </li>
                        )}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}
          </div>
        </div>
      </section>

      {/* Quality by Lot Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Quality by Lot</h2>
            <p className="text-xl text-gray-600">
              Every coffee we offer is handled as a lot-specific commercial product, not as a vague catalog item.
            </p>
          </div>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                Final specifications depend on the lot and are shared directly with qualified buyers upon request. Lot-specific information typically includes:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-amber-600" />
                    Process & Origin
                  </h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Processing method (washed or natural)</li>
                    <li>• Crop timing and harvest period</li>
                    <li>• Origin region and producer information</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-600" />
                    Physical Specifications
                  </h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Moisture content</li>
                    <li>• Water activity</li>
                    <li>• Density</li>
                    <li>• Screen size distribution</li>
                    <li>• Defect count (per 300g sample)</li>
                    <li>• Quaker count</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Sensory & Commercial
                  </h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Sensory summary</li>
                    <li>• Cup profile, intensity, balance</li>
                    <li>• Available volume</li>
                    <li>• Packaging options</li>
                    <li>• Incoterms (FOB, CIF, etc.)</li>
                    <li>• Export readiness</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed border-t border-slate-200 pt-6">This approach allows importers, traders, and roasters to make informed sourcing decisions based on actual coffee performance and commercial suitability, not marketing language.

              </p>
              
              <div className="mt-6 flex justify-center">
                <Button onClick={scrollToContact} className="bg-amber-600 hover:bg-amber-700 text-white">
                  Request Current Lot Info
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Custom Sourcing Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
            <CardHeader>
              <CardTitle className="text-4xl">Custom Sourcing Available</CardTitle>
              <CardDescription className="text-gray-300 text-xl">
                Our sourcing model is not limited to a fixed product list
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="">

              </p>
              
              <p className="text-gray-200 mb-6 leading-relaxed">
                We work with importers, traders, and roasters looking for:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold text-white mb-3 text-lg">Processing Options</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Washed coffees with varying preparation levels</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Natural coffees with process-driven fruit character and sweetness</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Custom process requirements based on application and target sensory profile</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white mb-3 text-lg">Sourcing Flexibility</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Cleaner preparation levels for quality-focused programs</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Specific cup profiles (fruity, citrus-forward, balanced)</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Arabica for blends, single-origin trials, or roasting programs</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Trial quantities and sample evaluation before container commitment</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>Container-based commercial supply with recurring order potential</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <p className="text-gray-200 leading-relaxed border-t border-slate-700 pt-6">
                This model is built for buyers who need more than off-the-shelf offerings. It's built for importers and roasters who require dependable execution on specific sourcing requests, with transparency on availability, quality, and commercial terms.
              </p>
              
              <div className="mt-6 flex gap-4 flex-wrap">
                <Button onClick={scrollToContact} className="bg-amber-600 hover:bg-amber-700 text-white">
                  Discuss Custom Sourcing
                </Button>
                <Button onClick={scrollToContact} variant="outline" className="bg-white/10 border-white text-white hover:bg-white/20">
                  Contact Us
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Work With Avila Trade Group */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Why Work With Avila Trade Group</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We built Avila Trade Group to serve importers, traders, and roasters who need dependable Venezuelan coffee access with commercial clarity and professional execution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
            {
              icon: Users,
              title: "Direct Origin Sourcing",
              description: "Direct relationships with Venezuelan producing regions for better pricing flexibility, supply reliability, and sourcing control. Not intermediaries, sourcing partners with on-the-ground coordination."
            },
            {
              icon: FileText,
              title: "Commercial Clarity",
              description: "Lot-specific quality documentation with transparent specifications, sensory summaries, and availability information. No vague marketing claims or generic product descriptions."
            },
            {
              icon: Target,
              title: "Flexible Matching",
              description: "Sourcing model built to match buyer requirements: washed or natural, commercial or higher-preparation, blend-focused or roasting programs, trial volumes or recurring supply."
            },
            {
              icon: Package,
              title: "Export Coordination",
              description: "Full export execution from Venezuelan origin to destination, including documentation, container logistics, quality control, and shipping terms (FOB, CIF, etc.)."
            },
            {
              icon: Handshake,
              title: "Long-Term Mindset",
              description: "Built for buyers seeking ongoing Venezuelan coffee access, not one-time transactions. Focused on partnership development, supply continuity, and mutual commercial success."
            }].
            map((feature) =>
            <Card key={feature.title} className="border-slate-200 hover:border-amber-400 transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <CardTitle className="text-xl text-slate-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Sample Request Section */}
      {showSampleForm &&
      <section id="sample-request" className="py-24 bg-amber-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Request Coffee Samples</h2>
              <p className="text-lg text-gray-600">
                Experience our quality first-hand. Request samples of our green coffee products.
              </p>
            </div>

            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <form onSubmit={handleSampleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="sample-name">Full Name *</Label>
                      <Input
                      id="sample-name"
                      required
                      value={sampleForm.name}
                      onChange={(e) => setSampleForm({ ...sampleForm, name: e.target.value })}
                      placeholder="John Doe" />

                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sample-company">Company *</Label>
                      <Input
                      id="sample-company"
                      required
                      value={sampleForm.company}
                      onChange={(e) => setSampleForm({ ...sampleForm, company: e.target.value })}
                      placeholder="Your Company Name" />

                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="sample-email">Email *</Label>
                      <Input
                      id="sample-email"
                      type="email"
                      required
                      value={sampleForm.email}
                      onChange={(e) => setSampleForm({ ...sampleForm, email: e.target.value })}
                      placeholder="john@company.com" />

                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sample-phone">Phone</Label>
                      <Input
                      id="sample-phone"
                      type="tel"
                      value={sampleForm.phone}
                      onChange={(e) => setSampleForm({ ...sampleForm, phone: e.target.value })}
                      placeholder="+1 234 567 8900" />

                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sample-country">Country *</Label>
                    <Input
                    id="sample-country"
                    required
                    value={sampleForm.country}
                    onChange={(e) => setSampleForm({ ...sampleForm, country: e.target.value })}
                    placeholder="Your Country" />

                  </div>

                  <div className="space-y-3">
                    <Label>Coffee Types * (Select one or more with defect levels)</Label>
                    <div className="space-y-2 border border-slate-200 rounded-lg p-4">
                      {coffeeProducts.map((product) => (
                        <div key={product.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`sample-coffee-${product.id}`}
                            checked={sampleForm.selectedCoffees.includes(`${product.shortName} - ${product.defects} defects`)}
                            onCheckedChange={() => handleCoffeeToggle(product.shortName, product.defects, 'sample')}
                          />
                          <label
                            htmlFor={`sample-coffee-${product.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {product.shortName} - {product.defects} defects ({product.grade})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sample-quantity">Estimated Monthly Volume</Label>
                    <Select value={sampleForm.quantity} onValueChange={(value) => setSampleForm({ ...sampleForm, quantity: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select volume range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-5 containers">1-5 containers</SelectItem>
                        <SelectItem value="5-10 containers">5-10 containers</SelectItem>
                        <SelectItem value="10+ containers">10+ containers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sample-notes">Additional Requirements</Label>
                    <Textarea
                    id="sample-notes"
                    value={sampleForm.additionalNotes}
                    onChange={(e) => setSampleForm({ ...sampleForm, additionalNotes: e.target.value })}
                    placeholder="Specific quality requirements, processing methods, or other details..."
                    rows={4} />

                  </div>

                  <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || sampleForm.selectedCoffees.length === 0}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white">

                    {isSubmitting ? 'Submitting...' : 'Request Samples'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      }

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Source Venezuelan Coffee?</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Request lot-specific specifications for currently available coffees, arrange sample evaluation, or discuss custom sourcing requirements.
          </p>
          <p className="text-lg text-gray-400 mb-12">
            We respond within 24 hours with availability, specifications, and pricing.
          </p>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">Whether you're an importer building a Venezuelan portfolio, a trader seeking flexible origin supply, or a roaster exploring new sourcing partnerships, Avila Trade Group provides dependable access to Venezuelan green coffee with commercial clarity and export-ready execution.

          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">

              Request Lot Specifications
            </Button>
            <Button
              size="lg"
              onClick={scrollToSample}
              className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">

              Request Samples
            </Button>
            <Button
              size="lg"
              onClick={scrollToContact}
              variant="outline"
              className="bg-white/10 border-white text-white hover:bg-white/20 text-lg px-8 py-6">

              Contact Avila Trade Group
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about Avila Trade Group and our Venezuelan green coffee supply
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left text-lg font-semibold">
                What does Avila Trade Group do?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Avila Trade Group is a Venezuelan green coffee sourcing and export company serving importers, traders, and roasters. We source, coordinate, and export Venezuelan green coffee—washed and natural arabica—with lot-specific specifications, flexible sourcing options, and export-ready logistics execution.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left text-lg font-semibold">
                What kind of coffee does Avila Trade Group offer?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                We offer Venezuelan green coffee across multiple preparation levels and processing methods, including washed arabica and natural arabica. Our portfolio includes Higher-Preparation Washed Arabica, Premium Commercial Washed Arabica, and custom sourcing options, with lot-specific specifications available for each offering.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left text-lg font-semibold">
                Do you provide coffee specifications by lot?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Yes. Every coffee we offer is documented with lot-specific specifications, including processing method (washed or natural), physical specs (moisture, density, screen size, defect count), sensory summary, available volume, packaging options, and logistics terms. Specifications are shared directly with qualified buyers upon request.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left text-lg font-semibold">
                Can you source coffee based on buyer requirements?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Yes. In addition to our core coffee range, we can source according to buyer specifications—target cup profile, processing method (washed or natural), preparation level, volume requirements, and end application. We work with importers, traders, and roasters seeking custom sourcing solutions from Venezuelan origin.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger className="text-left text-lg font-semibold">
                Do you offer washed and natural coffee?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Yes. Avila Trade Group supplies both washed arabica and natural arabica from Venezuelan origin. Our washed coffees are available across multiple preparation levels (higher-preparation and premium commercial), and our natural coffees are offered according to lot availability and buyer requirements through our custom sourcing model.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger className="text-left text-lg font-semibold">
                Do you only work with coffee?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Green coffee is our primary focus. As we grow, we intend to expand into select agricultural commodities, applying the same sourcing standards and export execution we use for coffee.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7">
              <AccordionTrigger className="text-left text-lg font-semibold">
                Who is your target customer?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                We work with coffee importers, coffee traders, and coffee roasters seeking reliable Venezuelan green coffee supply with lot-specific quality information and workable commercial terms. Our customers include buyers building origin portfolios, developing blends, managing roasting programs, and seeking long-term sourcing partnerships.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8">
              <AccordionTrigger className="text-left text-lg font-semibold">
                How do I request samples or specifications?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                Contact Avila Trade Group directly through our website contact form or email bestcoffee@avilatradegroup.com. We respond within 24 hours with current availability, lot specifications, and sample coordination for qualified buyers.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Get in Touch</h2>
              <p className="text-lg text-gray-600 mb-8">
                Ready to establish a reliable supply partnership? Contact us to discuss your requirements.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:bestcoffee@avilatradegroup.com" className="text-gray-600 hover:text-amber-600 transition-colors">
                      bestcoffee@avilatradegroup.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Offices</h3>
                    <p className="text-gray-600 mb-1">📍 Caracas, Venezuela</p>
                    <p className="text-gray-600">📍 Munich, Germany</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Export Markets</h3>
                    <p className="text-gray-600">Europe, Global</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-2xl">Send us a Message</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="John Doe" />

                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company *</Label>
                    <Input
                      id="company"
                      required
                      value={contactForm.company}
                      onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                      placeholder="Your Company Name" />

                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="john@company.com" />

                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="+1 234 567 8900" />

                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      required
                      value={contactForm.country}
                      onChange={(e) => setContactForm({ ...contactForm, country: e.target.value })}
                      placeholder="Your Country" />

                  </div>


                  <div className="space-y-2">
                    <Label htmlFor="inquiryType">What are you interested in? *</Label>
                    <Select value={contactForm.inquiryType} onValueChange={(value) => setContactForm({ ...contactForm, inquiryType: value, selectedCoffees: [] })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select inquiry type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="commercial-sheets">Request Commercial Sheets</SelectItem>
                        <SelectItem value="special-request">Special Coffee Request</SelectItem>
                        <SelectItem value="general">General Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {contactForm.inquiryType === 'commercial-sheets' && (
                    <div className="space-y-3">
                      <Label>Select Coffee Types * (Choose one or more)</Label>
                      <div className="space-y-2 border border-slate-200 rounded-lg p-4">
                        {coffeeProducts.map((product) => (
                          <div key={product.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`contact-coffee-${product.id}`}
                              checked={contactForm.selectedCoffees.includes(`${product.shortName} - ${product.defects} defects`)}
                              onCheckedChange={() => handleCoffeeToggle(product.shortName, product.defects, 'contact')}
                            />
                            <label
                              htmlFor={`contact-coffee-${product.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {product.shortName} - {product.defects} defects ({product.grade})
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="message">
                      {getMessageLabel(contactForm.inquiryType)} {isMessageRequired(contactForm.inquiryType) ? '*' : ''}
                    </Label>
                    <Textarea
                      id="message"
                      required={isMessageRequired(contactForm.inquiryType)}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder={getMessagePlaceholder(contactForm.inquiryType)}
                      rows={4} />

                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting || (contactForm.inquiryType === 'commercial-sheets' && contactForm.selectedCoffees.length === 0)}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white">

                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <p className="text-gray-400 mb-4 leading-relaxed">
                Avila Trade Group is a Venezuelan green coffee sourcing and export partner serving importers, traders, and roasters with lot-specific coffee offers, flexible sourcing across washed and natural processing methods, and export-ready execution from origin to destination.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 hover:text-amber-400 transition-colors">About Us</a></li>
                <li><a href="#products" className="text-gray-400 hover:text-amber-400 transition-colors">Products</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-amber-400 transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Avila Trade Group. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;